import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Forced first-login password change screen for parents whose account
 * was created by an admin with a temporary password.
 */
export default function ParentChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success("Password updated. Welcome to HTP Sports Hub!");
      nav("/parent");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="parent-change-password">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 border border-brand-blue/50 flex items-center justify-center brand-blue">
            <KeyRound size={18} />
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange">// First-time login</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none tracking-tighter">
          Set your<br /><span className="brand-blue">new password</span>.
        </h1>
        <p className="mt-4 text-sm text-white/60">
          Hi {user?.name?.split(" ")[0] || "there"} — please replace the temporary password
          shared by our office with a private one you'll remember.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="Temporary password from office" required
            data-testid="cp-current"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 6 chars)" required minLength={6}
            data-testid="cp-new"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Confirm new password" required minLength={6}
            data-testid="cp-confirm"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <button
            type="submit" disabled={loading}
            data-testid="cp-submit"
            className="w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50"
          >
            {loading ? "…" : "Update Password"}
          </button>
          <button
            type="button"
            onClick={async () => { await logout(); nav("/parent/login"); }}
            className="w-full text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white pt-2"
            data-testid="cp-logout"
          >
            Cancel & Logout
          </button>
        </form>
      </div>
    </div>
  );
}
